import { Text, Image, useSitecoreContext, LayoutServicePageState, LinkField } from '@sitecore-jss/sitecore-jss-nextjs';
import { useI18n } from 'next-localization';
import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';

import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import { dataFetcher } from 'lib/data-fetcher';
import type { ComponentProps } from 'lib/component-props';

import styles from './EventDetail.module.scss';
import mockData from './MockData/mock.data.event-details.json';
import { EventDetailResponse, EventOccurrence } from './Types/EventDetailResponse.Type';

import { useRouter } from 'next/router';
import Loader, { LoaderVariant } from 'components/helpers/Loader/Loader';
import Link from 'components/helpers/Link/Link';
import Icon from 'components/helpers/Icon';
import Button from 'components/helpers/Button/Button';

import { addQueryStringParameter, getCleanID, getQueryStringParameter, isValidSitecoreItemGUID } from 'lib/utils';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';

type EventDetailProps = ComponentProps & {
  fields: {
    //todo : After Event listing implementation we have to address It. This field can be used to implement related events sections or to display custom disclaimer text.
    Audience: Audience;
  };
};

export type Audience = {
  id: string;
  name: string;
};

const cx = classNames.bind(styles);

const EventDetail = (props: EventDetailProps): React.JSX.Element => {
  const { className } = props;

  const [events, setEvents] = useState<EventDetailResponse[]>([]);
  const [selectedOccurrence, setSelectedOccurrence] = useState<EventOccurrence>();
  const [occurrenceOnSameDate, setoccurrenceOnSameDate] = useState<EventOccurrence[]>([]);
  const [activeOccurrence, setActiveOccurrence] = useState<EventOccurrence>();
  const [registerError, setRegisterError] = useState('');
  const [hasOnDemand, setHasOnDemand] = useState<boolean>(false);

  const router = useRouter();
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();

  const [showNoResultsFound, setShowNoResultsFound] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(true);

  const getOnDemandVideo = (kalturaCode: string, playerId: string) => {
    if (typeof window !== 'undefined' && kalturaCode) {
      const scriptk = document.createElement('script');
      scriptk.src =
        'https://cdnapisec.kaltura.com/p/6105852/embedPlaykitJs/uiconf_id/55870672?autoembed=true&targetId=' +
        playerId +
        '&entry_id=' +
        kalturaCode;
      scriptk.type = 'text/javascript';
      scriptk.async = true;
      if (!document.querySelector(`script[src="${scriptk.src}"]`)) {
        document.body.appendChild(scriptk);
      }
    }
  };

  useEffect(() => {
    let eventItemId = '';
    let occurrenceId = '';
    let allParamsFound = false;

    if (sitecoreContext.pageState === LayoutServicePageState.Normal) {
      if (router.isReady) {
        if (router.asPath?.indexOf('?') > 0) {
          const eventGUID = getQueryStringParameter('eventId', router.query);
          const occurrenceGUID = getQueryStringParameter('occurrenceId', router.query);

          isValidSitecoreItemGUID(eventGUID.trim()) && (eventItemId = eventGUID);
          isValidSitecoreItemGUID(occurrenceGUID.trim()) && (occurrenceId = getCleanID(occurrenceGUID));

          if (eventItemId) {
            allParamsFound = true;
          }
        }
      }

      const baseUrl = process.env['PUBLIC_URL'] || '';
      if (allParamsFound) {
        dataFetcher<EventDetailResponse[]>(`${baseUrl}/api/events/getEventDetail`, {
          eventItemId: eventItemId,
          language: sitecoreContext.language,
        }).then((response) => {
          const $data = response.data;

          setEvents([...new Set($data)]);
          const hasOnDemand =
            $data[0]?.locationType?.targetItems?.some((item) => item.id === '72E8405490274AA386596430EAF72C68') ===
            true;
          setHasOnDemand(hasOnDemand);
          if (!hasOnDemand) {
            const eventOccurrences = $data[0]?.children?.EventOccurrences;

            const matchedOccurrence = eventOccurrences?.find((Occurrence) => Occurrence.id === occurrenceId);
            if (matchedOccurrence) {
              setSelectedOccurrence(matchedOccurrence);
              const eventDate = matchedOccurrence?.startDateTime?.jsonValue?.value as string;
              const matchedDate = new Date(eventDate)?.toLocaleDateString('en-CA').split('T')[0];
              const sameDateOccurrence = eventOccurrences
                ?.filter((eventOccurrences) => {
                  const occurrenceDate = new Date(eventOccurrences.startDateTime.jsonValue.value)
                    .toLocaleDateString('en-CA')
                    .split('T')[0];
                  return occurrenceDate === matchedDate;
                })
                ?.sort(
                  (a, b) =>
                    new Date(a.startDateTime.jsonValue.value).getTime() -
                    new Date(b.startDateTime.jsonValue.value).getTime()
                );

              setoccurrenceOnSameDate(sameDateOccurrence);
              sameDateOccurrence.length == 1 && setActiveOccurrence(matchedOccurrence);
            }
          }
          setShowLoader(false);
          setShowNoResultsFound($data.length === 0);
        });
      }
    } else {
      setEvents(mockData as unknown as EventDetailResponse[]);
      setShowLoader(false);
      setShowNoResultsFound((mockData as unknown as EventDetailResponse[]).length === 0);
    }
  }, [router, sitecoreContext]);

  const getEventStartDate = (startDateTime: string, includeYear: boolean) => {
    const StartDate = new Date(startDateTime);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    };
    if (includeYear) options.year = 'numeric';
    return StartDate.toLocaleDateString(sitecoreContext.language, options);
  };

  const getEventTime = (dateTime: string) => {
    if (dateTime != '0001-01-01T00:00:00Z') {
      const eventDate = new Date(dateTime);
      const eventTime = eventDate.toLocaleTimeString(sitecoreContext.language, {
        hour: 'numeric',
        minute: '2-digit',
      });
      return eventTime.toLowerCase().replace('am', 'a.m.').replace('pm', 'p.m.');
    }
    return;
  };

  const handleRegister = (occ: EventOccurrence) => {
    setActiveOccurrence(occ);
    setRegisterError('');
  };

  const handleRegisterClick = () => {
    const eventRegisterUrl = activeOccurrence?.registerUrl?.jsonValue;
    const registerHref = eventRegisterUrl?.value?.href;
    const isRegistrationForm = activeOccurrence?.isRegistrationForm?.boolValue;

    if (!registerHref) {
      setRegisterError(t('RegisterError'));
      return;
    }
    setRegisterError('');
    let url = registerHref;
    if (isRegistrationForm) {
      url = addQueryStringParameter(url, 'eventId', getQueryStringParameter('eventId', router.query));
      url = addQueryStringParameter(url, 'occurrenceId', activeOccurrence?.id);
    }

    if (eventRegisterUrl?.value.target === '_blank') {
      window.open(url);
    } else {
      window.location.href = url;
    }
  };

  const handleJoinNowClick = () => {
    const joinNowHref = activeOccurrence?.onlineEventUrl?.jsonValue;
    if (joinNowHref) handleEventClick(joinNowHref);
  };

  const handleEventClick = (EventHref: LinkField) => {
    const href = EventHref?.value?.href;
    if (!href) return;

    if (EventHref?.value.target === '_blank') {
      window.open(href);
    } else {
      window.location.href = href;
    }
  };

  const getEventButton = (activeOccurrence: EventOccurrence) => {
    const now = new Date();
    const eventStart = activeOccurrence?.startDateTime?.jsonValue?.value;
    const startDate = eventStart && eventStart != '0001-01-01T00:00:00Z' ? new Date(eventStart) : null;

    const eventEnd = activeOccurrence?.endDateTime?.jsonValue?.value;
    let endDate = eventEnd && eventEnd != '0001-01-01T00:00:00Z' ? new Date(eventEnd) : null;
    if (!endDate && startDate) {
      endDate = new Date(startDate);
      endDate.setHours(23, 59, 59, 999); //end of the start day
    }
    const isBefore = !!startDate && now < startDate;
    const isDuring = !!startDate && !!endDate && now >= startDate && now <= endDate;

    const hasValidRegister = activeOccurrence?.registerUrl?.jsonValue?.value?.href;
    const hasValidJoinNow = activeOccurrence?.onlineEventUrl?.jsonValue?.value?.href;

    let showRegister = false;
    let showJoinNow = false;

    if (hasValidRegister && hasValidJoinNow) {
      if (isBefore) showRegister = true;
      else if (isDuring) showJoinNow = true;
    } else if (hasValidRegister && !hasValidJoinNow) {
      if (isBefore || isDuring) showRegister = true;
    } else if (!hasValidRegister && hasValidJoinNow) {
      if (isDuring) showJoinNow = true;
    }
    return { showRegister, showJoinNow };
  };

  return (
    <div className={cx('event-detail', 'component-container-edge', className)}>
      {showLoader && (
        <div role="status">
          <span className={cx('screen-reader-only')}>{t('Event detail loaded')}</span>
          <Loader variant={LoaderVariant.Spinner} />
        </div>
      )}
      {!showLoader && showNoResultsFound && (
        <div role="status">
          <strong className={cx('no-event')}>{t('NoEventFound')}</strong>
        </div>
      )}
      {!showLoader && !showNoResultsFound && events?.length > 0 && (
        <>
          {events.map((event, index) => {
            const hasImage = Object.keys(event.image?.jsonValue?.value || {}).length > 0;
            const onDemandVideoKalturaCode = event.onDemandVideo?.value;
            const kalturaPlayerType = event.onDemandVideoPlayerType?.targetItem?.playerID?.value;
            const playerId = kalturaPlayerType ? 'kaltura_player_ ' + kalturaPlayerType : 'kaltura_player_55870672';

            if (onDemandVideoKalturaCode) getOnDemandVideo(onDemandVideoKalturaCode, playerId);
            const isCanceled = activeOccurrence?.canceled?.boolValue;
            const hasMultipleOccurrences = occurrenceOnSameDate?.length > 1;
            const hasValidRegisterHref = activeOccurrence?.registerUrl?.jsonValue?.value?.href;

            const registerDeadline = activeOccurrence?.registerDeadlineDate?.jsonValue?.value;
            const isPastRegisterDeadline =
              registerDeadline && registerDeadline !== '0001-01-01T00:00:00Z'
                ? new Date(registerDeadline) < new Date()
                : false;

            const showRegisterButton =
              !isCanceled &&
              !isPastRegisterDeadline &&
              ((!hasMultipleOccurrences && hasValidRegisterHref) ||
                (hasMultipleOccurrences && (!activeOccurrence || hasValidRegisterHref)));

            const eventButton = activeOccurrence ? getEventButton(activeOccurrence) : undefined;
            const onDemandHref = event?.onDemandUrl?.jsonValue;

            return (
              <React.Fragment key={index}>
                <section className={cx('event-header')}>
                  <Text tag="h1" field={event.name} encode={sitecoreContext.pageEditing} />
                  {selectedOccurrence?.startDateTime?.jsonValue?.value && (
                    <strong className={cx('event-date')}>
                      {getEventStartDate(selectedOccurrence?.startDateTime?.jsonValue?.value, true)}
                    </strong>
                  )}
                  <Text tag="div" field={event.type?.targetItem?.eventType} className={cx('event-type')} />
                </section>
                <section className={cx('event-listing')}>
                  {hasImage && onDemandVideoKalturaCode === '' && (
                    <Image className={cx('component-spacing__bottom--md')} field={event.image?.jsonValue} />
                  )}
                  {onDemandVideoKalturaCode != '' && (
                    <div className={cx('responsive_padding', 'video-padding', className)}>
                      <div className={cx('responsive_wrapper', 'video-wrapper')}>
                        <div id={playerId} style={{ width: '100%', height: '100%' }}></div>
                      </div>
                    </div>
                  )}
                </section>
                <section className={cx('event-listing')}>
                  {event.description?.value?.trim() && <h2>{t('EventDetails')}</h2>}
                  <RichTextWrapper field={event.description} />
                  {event.additionalResources?.value?.trim() && (
                    <h2 className={cx('component-spacing__top--md')}>{t('AdditionalResources')}</h2>
                  )}
                  <RichTextWrapper field={event.additionalResources} />
                </section>
                <>
                  <section
                    className={cx('occurrence-listing', { 'no-image': !hasImage && onDemandVideoKalturaCode === '' })}
                  >
                    {!hasOnDemand && (
                      <>
                        <section>
                          {occurrenceOnSameDate.length > 1 && (
                            <>
                              <div className={cx('occurrence-select')}>
                                <p>{t('SelectTimeDesc')}</p>
                                <ul>
                                  {occurrenceOnSameDate.map((occ, i) => (
                                    <li key={i}>
                                      <Button
                                        className={cx('occ-time-button', activeOccurrence?.id === occ.id && 'active')}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleRegister(occ);
                                        }}
                                      >
                                        {occ.canceled.boolValue && <Icon variant="error" />}
                                        {getEventTime(occ?.startDateTime?.jsonValue?.value)}
                                      </Button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              {registerError && (
                                <div
                                  className={cx('register-error', 'icon-wrapper')}
                                  aria-label={t('Aria-Label-Register-Error')}
                                >
                                  <Icon variant="error" /> {registerError}
                                </div>
                              )}
                            </>
                          )}
                        </section>
                        <section className={cx('occurrence-details')}>
                          {activeOccurrence?.canceled?.boolValue && (
                            <p className={cx('canceled-info')}>
                              <Icon variant="error" />
                              {t('CanceledText')}
                            </p>
                          )}
                          {selectedOccurrence && (
                            <>
                              <div className={cx('icon-wrapper', 'no-mrgn')}>
                                <Icon variant="calendar" className={cx('event-icon')} /> <span>{t('DateTime')} </span>
                              </div>
                              {selectedOccurrence?.startDateTime?.jsonValue?.value && (
                                <span className={cx('event-date', 'occurrence-info')}>
                                  {getEventStartDate(selectedOccurrence?.startDateTime?.jsonValue?.value, true)}
                                </span>
                              )}
                              {occurrenceOnSameDate.map((occ, i) => (
                                <React.Fragment key={i}>
                                  <div className={cx('occurrence-info')}>
                                    {getEventTime(occ?.startDateTime?.jsonValue?.value)}
                                    {occ?.endDateTime?.jsonValue?.value != '0001-01-01T00:00:00Z' && (
                                      <> - {getEventTime(occ?.endDateTime?.jsonValue?.value)}</>
                                    )}
                                  </div>
                                </React.Fragment>
                              ))}
                            </>
                          )}
                          {event.inPersonEventLocation?.targetItem && (
                            <>
                              <div className={cx('icon-wrapper')}>
                                <Icon variant="location" className={cx('event-icon')} /> <span>{t('Location')}</span>
                              </div>
                              <div className={cx('occurrence-info')}>
                                <Text
                                  tag="div"
                                  field={event.inPersonEventLocation?.targetItem?.locationName}
                                  encode={sitecoreContext.pageEditing}
                                />
                                <Text
                                  tag="div"
                                  field={event.inPersonEventLocation?.targetItem?.addressLine1}
                                  encode={sitecoreContext.pageEditing}
                                />
                                {event.inPersonEventLocation?.targetItem?.addressLine2?.value && (
                                  <Text
                                    tag="div"
                                    field={event.inPersonEventLocation?.targetItem?.addressLine2}
                                    encode={sitecoreContext.pageEditing}
                                  />
                                )}
                                <div>
                                  {event.inPersonEventLocation?.targetItem?.city?.value}
                                  {event.inPersonEventLocation?.targetItem?.state?.value && ', '}
                                  {event.inPersonEventLocation?.targetItem?.state?.value}
                                  {event.inPersonEventLocation?.targetItem?.zipCode?.value && ' '}
                                  {event.inPersonEventLocation?.targetItem?.zipCode?.value}
                                </div>
                                <Link
                                  field={event.inPersonEventLocation?.targetItem?.directionsLink?.jsonValue}
                                  icon="external-link"
                                  showLinkTextWithChildrenPresent={false}
                                  target={
                                    event.inPersonEventLocation?.targetItem?.directionsLink?.jsonValue?.value?.target ||
                                    '_blank'
                                  }
                                >
                                  {t('GetDirections')}
                                </Link>
                              </div>
                            </>
                          )}

                          {event.onlineEventSpecialInstructions.value && (
                            <>
                              <div className={cx('icon-wrapper')}>
                                <Icon variant="video" className={cx('event-icon')} /> <span>{t('OnlineEvent')} </span>
                              </div>
                              <Text
                                tag="p"
                                field={event.onlineEventSpecialInstructions}
                                className={cx('occurrence-info')}
                              />
                            </>
                          )}
                          {event.host?.targetItem && (
                            <>
                              <div className={cx('icon-wrapper')}>
                                <Icon variant="person" className={cx('event-icon')} /> <span>{t('HostedBy')} </span>
                              </div>

                              <Link
                                field={event.host?.targetItem?.hostUrl?.jsonValue}
                                showLinkTextWithChildrenPresent={false}
                                target={event.host?.targetItem?.hostUrl?.jsonValue?.value?.target || '_blank'}
                                className={cx('occurrence-info')}
                              >
                                <Text field={event.host?.targetItem?.hostName} encode={sitecoreContext.pageEditing} />
                              </Link>
                            </>
                          )}
                        </section>
                        {eventButton?.showRegister && showRegisterButton && (
                          <Button onClick={handleRegisterClick} className="w-100">
                            <Text
                              tag="span"
                              field={
                                activeOccurrence?.registerLinkButtonText?.value?.trim()
                                  ? activeOccurrence?.registerLinkButtonText
                                  : { value: t('RegisterNow') }
                              }
                              encode={sitecoreContext.pageEditing}
                            />
                          </Button>
                        )}

                        {eventButton?.showJoinNow && (
                          <Button onClick={handleJoinNowClick} className="w-100">
                            <Text
                              tag="span"
                              field={
                                activeOccurrence?.onlineEventLinkButtonText?.value?.trim()
                                  ? activeOccurrence?.onlineEventLinkButtonText
                                  : { value: t('JoinNow') }
                              }
                              encode={sitecoreContext.pageEditing}
                            />
                          </Button>
                        )}
                      </>
                    )}
                    {hasOnDemand && (
                      <>
                        <section className={cx('occurrence-details')}>
                          {event.onDemandSpecialInstructions?.value && (
                            <>
                              <div className={cx('icon-wrapper')}>
                                <Icon variant="play" className={cx('event-icon')} /> <span>{t('OnDemandEvent')} </span>
                              </div>
                              <Text
                                tag="p"
                                field={event.onDemandSpecialInstructions}
                                className={cx('occurrence-info')}
                              />
                            </>
                          )}

                          {event.host?.targetItem && (
                            <>
                              <div className={cx('icon-wrapper')}>
                                <Icon variant="person" className={cx('event-icon')} /> <span>{t('HostedBy')} </span>
                              </div>

                              <Link
                                field={event.host?.targetItem?.hostUrl?.jsonValue}
                                showLinkTextWithChildrenPresent={false}
                                target={event.host?.targetItem?.hostUrl?.jsonValue?.value?.target || '_blank'}
                                className={cx('occurrence-info')}
                              >
                                <Text field={event.host?.targetItem?.hostName} encode={sitecoreContext.pageEditing} />
                              </Link>
                            </>
                          )}
                        </section>
                        {onDemandHref?.value?.href && (
                          <Button onClick={() => handleEventClick(onDemandHref)} className="w-100">
                            <Text
                              tag="span"
                              field={
                                event?.onDemandLinkButtonText?.value?.trim()
                                  ? event?.onDemandLinkButtonText
                                  : { value: t('AttendEvent') }
                              }
                              encode={sitecoreContext.pageEditing}
                            />
                          </Button>
                        )}
                      </>
                    )}
                  </section>
                </>
              </React.Fragment>
            );
          })}
        </>
      )}
    </div>
  );
};

export default compose<EventDetailProps>(withStyles())(EventDetail);
